import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetApprovalApplications } from '../../../../api/approval-applications/approval-applications';
import { ApprovalApplicationListItem } from '../../../../api/model';
import POST_MESSAGES from '../../../../constants/postMessages';
import { useDefaultOptions } from '../../../../hooks/api';
import usePostMessage from '../../../../hooks/usePostMessage';
import ErrorComponent from '../../../atoms/ErrorComponent';
import Link from '../../../atoms/Link';
import WaitComponent from '../../../atoms/WaitComponent';
import { ApprovalApplicationStatuses, ApprovalApplicationTypes } from './ProductionApprovalApplicationsTab';

interface Props {
  productionId: number;
}

/**
 * 承認タブの承認一覧画面
 * @param param0
 * @returns
 */
function ProductionApprovalApplicationList({ productionId }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // 一覧取得
  const options = useDefaultOptions(language);

  const {
    data: getApprovalApplicationsResponse,
    isError,
    error,
    isLoading,
    refetch: refetchApprovalApplications,
  } = useGetApprovalApplications(productionId, undefined, options);

  const [sortInfo, setSortInfo] = useState({ sortkey: '' as keyof ApprovalApplicationListItem, isDesc: false });

  const approvalAppricationListItems = useMemo(
    () =>
      (getApprovalApplicationsResponse?.data.approvalAppricationListItems ?? []).sort((a, b) => {
        const propertyValueA = a[sortInfo.sortkey] ?? '';
        const propertyValueB = b[sortInfo.sortkey] ?? '';
        const direction = sortInfo.isDesc ? -1 : 1;

        if (propertyValueA < propertyValueB) {
          return -1 * direction;
        }
        if (propertyValueA > propertyValueB) {
          return 1 * direction;
        }
        return 0;
      }),
    [getApprovalApplicationsResponse?.data.approvalAppricationListItems, sortInfo.isDesc, sortInfo.sortkey]
  );

  // 承認申請画面からの更新通知受信
  usePostMessage(POST_MESSAGES.ReloadApprovalApplicationList, async () => {
    await refetchApprovalApplications();
  });

  // ソートクリック時の処理
  const onSortClicked = useCallback((key: keyof ApprovalApplicationListItem) => {
    setSortInfo((preSortInfo) => ({ sortkey: key, isDesc: preSortInfo.sortkey === key && !preSortInfo.isDesc }));
  }, []);

  // 行毎のメニュー開閉処理
  const [approvalApplicationIdForOpenedMenu, setApprovalApplicationIdForOpenedMenu] = useState('');

  const onOpenMenuClicked = useCallback(
    (approvalApplicationId: string) =>
      approvalApplicationId === approvalApplicationIdForOpenedMenu
        ? setApprovalApplicationIdForOpenedMenu('')
        : setApprovalApplicationIdForOpenedMenu(approvalApplicationId),
    [approvalApplicationIdForOpenedMenu]
  );

  if (isLoading) {
    return <WaitComponent />;
  }

  if (isError) {
    return <ErrorComponent error={error} />;
  }

  return (
    <>
      <div className="master-navi">
        <ul>
          <li className="detailbtn1-8b">
            <Link
              to={`/${language}/productions/edit/${productionId}/approval-application/add/${ApprovalApplicationTypes.approval}`}
              target="approval-application"
            >
              <i className="fa-solid fa-circle-check" />
              {t('承認申請')}
            </Link>
          </li>
          <li className="">
            <Link
              to={`/${language}/productions/edit/${productionId}/approval-application/add/${ApprovalApplicationTypes.deletion}`}
              target="approval-application"
            >
              <i className="fa-solid fa-circle-xmark" />
              {t('削除申請')}
            </Link>
          </li>
          <li className="">
            <Link
              to={`/${language}/productions/edit/${productionId}/approval-application/add/${ApprovalApplicationTypes.reEditing}`}
              target="approval-application"
            >
              <i className="fa-solid fa-exclamation" />
              {t('再編集申請')}
            </Link>
          </li>
        </ul>
      </div>
      <div className="manuscript">
        <table className="table17 mt20">
          <tbody>
            <tr>
              <th>
                {t('ステータス')}
                <button
                  type="button"
                  className="text-button"
                  onClick={() => onSortClicked('approval_application_status_name')}
                >
                  <i className="fa-solid fa-sort" />
                </button>
              </th>
              <th>
                {t('ゲート')}
                <button
                  type="button"
                  className="text-button"
                  onClick={() => onSortClicked('approval_application_gate_name')}
                >
                  <i className="fa-solid fa-sort" />
                </button>
              </th>
              <th>
                {t('申請者')}
                <button type="button" className="text-button" onClick={() => onSortClicked('application_user_name')}>
                  <i className="fa-solid fa-sort" />
                </button>
              </th>
              <th>
                {t('申請日')}
                <button type="button" className="text-button" onClick={() => onSortClicked('application_on')}>
                  <i className="fa-solid fa-sort" />
                </button>
              </th>
              <th>
                {t('承認者1')}
                <button type="button" className="text-button" onClick={() => onSortClicked('approval1_user_name')}>
                  <i className="fa-solid fa-sort" />
                </button>
              </th>
              <th>
                {t('承認日1')}
                <button type="button" className="text-button" onClick={() => onSortClicked('approval1_on')}>
                  <i className="fa-solid fa-sort" />
                </button>
              </th>
              <th>
                {t('承認者2')}
                <button type="button" className="text-button" onClick={() => onSortClicked('approval2_user_name')}>
                  <i className="fa-solid fa-sort" />
                </button>
              </th>
              <th>
                {t('承認日2')}
                <button type="button" className="text-button" onClick={() => onSortClicked('approval2_on')}>
                  <i className="fa-solid fa-sort" />
                </button>
              </th>
              <th> </th>
            </tr>
            {approvalAppricationListItems.map((x) => (
              <tr key={x.approval_application_id}>
                <td>{x.approval_application_status_name}</td>
                <td>{x.approval_application_gate_name}</td>
                <td>{x.application_user_name}</td>
                <td>{x.application_on}</td>
                <td>{x.approval1_user_name}</td>
                <td>{x.approval1_on}</td>
                <td>{x.approval2_user_name}</td>
                <td>{x.approval2_on}</td>
                <td className="elloutput">
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => onOpenMenuClicked(x.approval_application_id ?? '')}
                  >
                    <i className="fa-solid fa-ellipsis-vertical" />
                  </button>
                  <div className={approvalApplicationIdForOpenedMenu === x.approval_application_id ? 'open' : ''}>
                    <span className="detailbtn1-8c">
                      <Link
                        to={`/${language}/productions/edit/${productionId}/approval-application/${
                          x.approval_application_status_id === ApprovalApplicationStatuses.approved ? 'view' : 'edit'
                        }/${x.approval_application_id ?? ''}`}
                        target="approval-application"
                      >
                        <i className="fa-solid fa-pen-to-square" />
                        {x.approval_application_status_id === ApprovalApplicationStatuses.approved
                          ? t('確認')
                          : t('編集')}
                      </Link>
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default ProductionApprovalApplicationList;
